import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../services/axiosInstance';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    Button,
    LinearProgress,
    CircularProgress
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const LEVELS = ['Novice', 'Beginner', 'Intermediate', 'Proficient', 'Advanced'];

// Component to parse markdown links and add icons
const QuestionText = ({ children, ...typographyProps }) => {
    const parseLinks = (text) => {
        if (!text || typeof text !== 'string') return text;

        const parts = [];
        let lastIndex = 0;
        const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        let match;

        while ((match = linkRegex.exec(text)) !== null) {
            // Add text before the link
            if (match.index > lastIndex) {
                parts.push(text.slice(lastIndex, match.index));
            }

            // Add the icon and link
            parts.push(
                <span key={`icon-${match.index}`} style={{ display: 'inline' }}>
                    <PictureAsPdfIcon style={{
                        fontSize: 16,
                        transform: 'translateY(2px)',
                        marginRight: '4px',
                        color: '#d32f2f'
                    }} />
                    <a
                        href={match[2]}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            textDecoration: 'none',
                            color: '#1976d2'
                        }}
                    >
                        {match[1]}
                    </a>
                </span>
            );

            lastIndex = match.index + match[0].length;
        }

        // Add remaining text after the last link
        if (lastIndex < text.length) {
            parts.push(text.slice(lastIndex));
        }

        return parts.length > 0 ? parts : text;
    };

    return (
        <Typography {...typographyProps}>
            {parseLinks(children)}
        </Typography>
    );
};

const QuestionPage = () => {
    const { userId, application } = useParams();
    const navigate = useNavigate();

    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [allAnswers, setAllAnswers] = useState({});
    const [assessmentComplete, setAssessmentComplete] = useState(false);
    const [saving, setSaving] = useState(false);
    const isProgressive = application === 'resource-capability';
    const markedComplete = useRef(false);

    const [currentTopicIndex, setCurrentTopicIndex] = useState(0);
    const [currentSubtopicIndex, setCurrentSubtopicIndex] = useState(0);
    const [currentLevelIndex, setCurrentLevelIndex] = useState(0);
    const [currentIndex, setCurrentIndex] = useState(0);

    const [topicsLoaded, setTopicsLoaded] = useState(false);
    // Tracks a failed (or empty) question fetch so we can show an error state
    // instead of silently rendering an empty page with a live Continue button.
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        const startAssessment = async () => {
            try {
                await axiosInstance.post('/users/start-assessment', { userId, application });
            } catch (err) {
                console.error('Failed to start assessment:', err);
            }
        };

        const fetchQuestions = async () => {
            try {
                const res = await axiosInstance.get(`/questions/test-question/${userId}/${application}`);
                const validQuestions = res.data.filter(
                    q => q.topic && q.subtopic && (!isProgressive || LEVELS.includes(q.level))
                );
                validQuestions.sort((a, b) =>
                    a.topic.localeCompare(b.topic) ||
                    a.subtopic.localeCompare(b.subtopic) ||
                    LEVELS.indexOf(a.level) - LEVELS.indexOf(b.level)
                );
                setQuestions(validQuestions);
                setLoadError(false);
            } catch (err) {
                // Transient gateway/network failures land here. Flag the error so the
                // render gate shows a retry screen rather than an empty page.
                console.error('Failed to fetch questions:', err);
                setLoadError(true);
            } finally {
                // Always mark loading as finished, success or failure, so we never
                // get stuck on the loading state after a failed fetch.
                setTopicsLoaded(true);
            }
        };

        const fetchProgress = async () => {
            try {
                const res = await axiosInstance.get(`/users/progress/${userId}/${application}`);
                const progress = res.data.progress;
                if (progress) {
                    if (isProgressive) {
                        setCurrentTopicIndex(progress.topicIndex ?? 0);
                        setCurrentSubtopicIndex(progress.subtopicIndex ?? 0);
                        setCurrentLevelIndex(progress.levelIndex ?? 0);
                    } else {
                        setCurrentIndex(progress.index || 0);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch progress:', err);
            }
        };

        const fetchSavedAnswers = async () => {
            try {
                const res = await axiosInstance.get(`/answers/${userId}/${application}`);
                const storedAnswers = {};
                res.data.forEach(a => {
                    storedAnswers[a.questionId] = a.answer;
                });
                setAllAnswers(storedAnswers);
            } catch (err) {
                console.error('Failed to load saved answers:', err);
            }
        };

        startAssessment();
        fetchQuestions();
        fetchProgress();
        fetchSavedAnswers();
    }, [userId, application, isProgressive]);

    useEffect(() => {
        const checkStatus = async () => {
            try {
                const res = await axiosInstance.get(`/users/status/${userId}/${application}`);
                if (res.data.status === -1) setAssessmentComplete(true);
            } catch (err) {
                console.error('Error checking status:', err);
            }
        };
        checkStatus();
    }, [userId, application]);

    useEffect(() => {
        if (assessmentComplete) {
            const timeout = setTimeout(() => navigate(`/${userId}`), 5000);
            return () => clearTimeout(timeout);
        }
    }, [assessmentComplete, navigate, userId]);


    const saveAnswer = async (questionId, answer, role) => {
        try {
            await axiosInstance.post(`/answers/${application}/create`, {
                answerId: questionId + userId,
                questionId,
                userId,
                answer,
                role
                // removed date - PostgreSQL sets it automatically
            });
            setAllAnswers(prev => ({ ...prev, [questionId]: answer }));
        } catch (err) {
            console.error('Failed to save answer:', err);
        }
    };

    const saveProgress = async (progressData) => {
        try {
            await axiosInstance.post('/users/save-progress', { userId, application, progressData });
        } catch (err) {
            console.error('Failed to save progress:', err);
        }
    };

    const markCompleteInDB = async () => {
        try {
            await axiosInstance.post('/users/complete-assessment', { userId, application });
        } catch (err) {
            console.error('Failed to mark assessment complete:', err);
        }
    };

    const calculateProgress = () => {
        if (!questions.length) return 0;

        if (isProgressive) {
            const totalSubtopics = new Set(questions.map(q => `${q.topic}|${q.subtopic}`)).size;
            const completed = new Set();
            for (const qId in allAnswers) {
                const question = questions.find(q => q.questionId === qId);
                if (question) completed.add(`${question.topic}|${question.subtopic}`);
            }
            return (completed.size / totalSubtopics) * 100;
        } else {
            const answered = Object.keys(allAnswers).length;
            return (answered / questions.length) * 100;
        }
    };

    const progressValue = calculateProgress();

    if (assessmentComplete) {
        return (
            <Box mt={10} textAlign="center">
                <Typography variant="h5" fontWeight="bold" mb={2}>Assessment complete!</Typography>
                <Typography variant="body1">You will be redirected to the homepage shortly...</Typography>
            </Box>
        );
    }

    // --- Render gates: keep the user out of the question UI until we actually
    // --- have questions. This is what prevents the empty page + false-complete bug.

    // Still waiting on the questions fetch to resolve.
    if (!topicsLoaded) {
        return (
            <Box mt={10} display="flex" flexDirection="column" alignItems="center" gap={2}>
                <CircularProgress />
                <Typography variant="body1">Loading your assessment…</Typography>
            </Box>
        );
    }

    // Fetch failed, or returned no questions for this user/application. Either way,
    // do not render an interactive page — show a recoverable error instead.
    if (loadError || questions.length === 0) {
        return (
            <Box mt={10} px={3} textAlign="center">
                <Typography variant="h6" fontWeight="bold" mb={2}>
                    We couldn't load your questions
                </Typography>
                <Typography variant="body1" mb={3}>
                    This is usually a temporary connection issue. Please refresh to try again.
                    If it keeps happening, contact support.
                </Typography>
                <Box display="flex" justifyContent="center" gap={2}>
                    <Button variant="contained" onClick={() => window.location.reload()}>
                        Refresh
                    </Button>
                    <Button variant="outlined" onClick={() => navigate(`/${userId}`)}>
                        Back to Homepage
                    </Button>
                </Box>
            </Box>
        );
    }

    if (isProgressive && topicsLoaded) {
        const groupBy = (arr, key) => Array.isArray(arr) ? arr.reduce((acc, item) => {
            const k = item[key];
            acc[k] = acc[k] || [];
            acc[k].push(item);
            return acc;
        }, {}) : {};

        const topics = groupBy(questions, 'topic');
        const topicKeys = Object.keys(topics);

        if (questions.length && topicKeys.length && currentTopicIndex >= topicKeys.length) {
            if (!markedComplete.current) {
                markedComplete.current = true;
                markCompleteInDB();
                setAssessmentComplete(true);
            }
            return null;
        }

        const topic = topicKeys[currentTopicIndex];
        const subtopics = groupBy(topics[topic], 'subtopic');
        const subtopicKeys = Object.keys(subtopics);

        if (currentSubtopicIndex >= subtopicKeys.length) {
            setCurrentSubtopicIndex(0);
            setCurrentTopicIndex(prev => prev + 1);
            return null;
        }

        const subtopic = subtopicKeys[currentSubtopicIndex];
        const questionsInSubtopic = subtopics[subtopic];
        const currentQuestion = questionsInSubtopic.find(q => q.level === LEVELS[currentLevelIndex]);

        if (!currentQuestion) {
            setCurrentLevelIndex(0);
            setCurrentSubtopicIndex(prev => prev + 1);
            return null;
        }

        const handleRadio = async (value) => {
            if (saving) return;
            setSaving(true);
            try {
                await saveAnswer(currentQuestion.questionId, value, currentQuestion.role);
                setAnswers({});
                await saveProgress({
                    topicIndex: currentTopicIndex,
                    subtopicIndex: currentSubtopicIndex,
                    levelIndex: value === 'yes' ? currentLevelIndex + 1 : 0
                });

                if (value === 'yes') {
                    setCurrentLevelIndex(prev => prev + 1);
                } else {
                    setCurrentLevelIndex(0);
                    setCurrentSubtopicIndex(prev => prev + 1);
                }
            } finally {
                setSaving(false);
            }
        };

        return (
            <Box mt={10} px={3} pb={10}>
                <Box sx={{ position: 'absolute', top: 80, right: 40, width: 300 }}>
                    <Typography variant="body2" fontWeight="medium">Progress</Typography>
                    <LinearProgress
                        variant="determinate"
                        value={progressValue}
                        sx={{ height: 8, borderRadius: 1 }}
                    />
                </Box>
                <Typography variant="h6" fontWeight="bold">{topic}</Typography>
                <Typography variant="subtitle1" fontStyle="italic" mb={3}>{subtopic}</Typography>
                <Card elevation={3}>
                    <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <QuestionText>{currentQuestion.question}</QuestionText>
                            <RadioGroup
                                row
                                value={answers[currentQuestion.questionId] || ''}
                                onChange={(e) => handleRadio(e.target.value)}
                            >
                                <FormControlLabel value="yes" control={<Radio disabled={saving} />} label="Yes" />
                                <FormControlLabel value="no" control={<Radio disabled={saving} />} label="No" />
                            </RadioGroup>
                        </Box>
                    </CardContent>
                </Card>
                <Box mt={3} display="flex" justifyContent="flex-start">
                    <Button
                        variant="outlined"
                        onClick={() => navigate(`/${userId}`)}
                    >
                        Back to Homepage
                    </Button>
                </Box>
            </Box>
        );
    }

    const currentSubtopic = questions[currentIndex]?.subtopic;
    const topic = questions[currentIndex]?.topic;
    const subtopicQuestions = questions.filter(q => q.subtopic === currentSubtopic);
    // Require at least one question — [].every() is vacuously true, which would
    // otherwise enable Continue on an empty page.
    const allAnswered =
        subtopicQuestions.length > 0 &&
        subtopicQuestions.every(q => answers[q.questionId]);

    const handleContinue = async () => {
        // Defence-in-depth: never act on an empty question set, even if some other
        // path manages to reach here.
        if (subtopicQuestions.length === 0) return;

        for (const q of subtopicQuestions) {
            const answer = answers[q.questionId];
            if (answer) {
                await saveAnswer(q.questionId, answer, q.role);
            }
        }

        const newIndex = currentIndex + subtopicQuestions.length;
        await saveProgress({ index: newIndex });
        setCurrentIndex(newIndex);
        setAnswers({});

        // Guard the completion check against an empty list (0 >= 0 is true).
        if (questions.length > 0 && newIndex >= questions.length) {
            await markCompleteInDB();
            setAssessmentComplete(true);
        }
    };

    return (
        <Box mt={10} px={3} pb={10}>
            <Box sx={{ position: 'absolute', top: 80, right: 40, width: 250 }}>
                <Typography variant="body2">Progress</Typography>
                <LinearProgress
                    variant="determinate"
                    value={progressValue}
                    sx={{ height: 8, borderRadius: 1 }}
                />
            </Box>
            {topic && <Typography variant="h6" fontWeight="bold">{topic}</Typography>}
            {currentSubtopic && (
                <Typography variant="subtitle1" fontStyle="italic" mb={3}>{currentSubtopic}</Typography>
            )}
            {subtopicQuestions.map(q => (
                <Card key={q.questionId} elevation={3} sx={{ mb: 3 }}>
                    <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <QuestionText>{q.question}</QuestionText>
                            <RadioGroup
                                //row
                                value={answers[q.questionId] || ''}
                                onChange={(e) =>
                                    setAnswers(prev => ({ ...prev, [q.questionId]: e.target.value }))
                                }
                            >
                                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                                <FormControlLabel value="no" control={<Radio />} label="No" />
                            </RadioGroup>
                        </Box>
                    </CardContent>
                </Card>
            ))}
            <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                <Button
                    variant="outlined"
                    onClick={() => navigate(`/${userId}`)}
                >
                    Back to Homepage
                </Button>
                <Button variant="contained" color="primary" onClick={handleContinue}
                    disabled={!allAnswered}>
                    Continue
                </Button>
            </Box>
        </Box>
    );
};

export default QuestionPage;